import { useEffect, useState } from "react";

import { useActivityClient, ActivityItem } from "@atlassian/recent-work-client";

import { useUserContext } from "@/components/user-context";
import { useBxpWebStargateClient } from "@/libraries/bxp-web-stargate-client/src/index";

const GRAPHQL_GATEWAY_URL =
    process.env["NODE_ENV"] === "production"
        ? "https://www.atlassian.com/gateway/api/graphql"
        : "https://wac.stg.internal.atlassian.com/gateway/api/graphql";

const START_URL =
    process.env["NODE_ENV"] === "production"
        ? "https://start.atlassian.com"
        : "https://start.stg.atlassian.com";

const constructLoginUrl = (url: string) => {
    const loginUrl = new URL(url);
    loginUrl.pathname = "/login.jsp";
    const searchParam = new URLSearchParams();
    const browseUrl = new URL(loginUrl);
    browseUrl.pathname = "/browse";
    searchParam.append("os_destination", browseUrl.href);
    loginUrl.search = searchParam.toString();
    return loginUrl;
};

// @ts-ignore
const getSitenameFromUrl = (url: string) => {
    try {
        const tempUrl = new URL(url);
        return tempUrl.hostname.split(".")[0] || "";
    } catch {
        return "";
    }
};

export interface UserProfileExistingSites {
    cloudId: string;
    displayName: string;
    url: string;
    hasAdminAccess: boolean;
    siteCreatedAt: number;
    products: {
        productName: string;
        productKey: string;
        productEdition: string;
    }[];
}

const filterSitesByProductKey = (
    sites: UserProfileExistingSites[] | undefined,
    productKeyFilter: string
): UserProfileExistingSites[] => {
    if (sites) {
        const filteredSites =
            sites.filter((site) => {
                return site.products.some((product) => {
                    return product.productKey.includes(productKeyFilter);
                });
            }) || [];

        return filteredSites;
    } else {
        return [];
    }
};

export interface UseRecentProductSiteOptions {
    productKeyFilter: string;
    activityClientProduct?: string;
}

export const useRecentProductSite = ({
    productKeyFilter,
    activityClientProduct = "jira"
}: UseRecentProductSiteOptions) => {
    const { profile, user } = useUserContext();

    const splitName = user.name?.split(" ");

    // @ts-ignore
    const stargateClient = useBxpWebStargateClient();
    const activityClient = useActivityClient(
        "v3",
        "www.atlassian.com",
        undefined,
        GRAPHQL_GATEWAY_URL
    );

    const availableProductSites = filterSitesByProductKey(
        profile.attributes?.existingSites,
        productKeyFilter
    );

    const hasMultipleSites = availableProductSites.length > 1;

    const [siteName, setSiteName] = useState("");
    const [siteUrl, setSiteUrl] = useState("");
    const [siteInitials, setSiteInitials] = useState("");
    const [cloudSiteId, setCloudSiteId] = useState<string | undefined>();

    useEffect(() => {
        const promises = [];
        let mostRecentActivity: ActivityItem | undefined;
        const fetchActivitiesPromise = activityClient
            .fetchActivities(
                [50, 125, 250],
                {
                    limit: 5,
                    products: [activityClientProduct]
                },
                ["workedOn", "viewed"]
            )
            .then((data) => {
                // Filter activities to only include those from available product sites
                const filteredViewed = data.viewed.filter((activity) =>
                    availableProductSites.some(
                        (site) =>
                            site.cloudId ===
                            activity.object.rootContainerIdentifier.id
                    )
                );
                const filteredWorkedOn = data.workedOn.filter((activity) =>
                    availableProductSites.some(
                        (site) =>
                            site.cloudId ===
                            activity.object.rootContainerIdentifier.id
                    )
                );

                const mostRecentlyViewed = filteredViewed[0];
                const mostRecentlyWorkedOn = filteredWorkedOn[0];

                if (mostRecentlyViewed && mostRecentlyWorkedOn) {
                    mostRecentActivity =
                        new Date(mostRecentlyViewed.timestamp) >
                        new Date(mostRecentlyWorkedOn.timestamp)
                            ? mostRecentlyViewed
                            : mostRecentlyWorkedOn;
                } else if (mostRecentlyWorkedOn) {
                    mostRecentActivity = mostRecentlyWorkedOn;
                } else if (mostRecentlyViewed) {
                    mostRecentActivity = mostRecentlyViewed;
                }
            })
            .catch((error) => console.log(error));
        promises.push(fetchActivitiesPromise);

        Promise.allSettled(promises).then(() => {
            if (mostRecentActivity) {
                const loginUrl = constructLoginUrl(
                    mostRecentActivity.object.url
                );
                setSiteUrl(loginUrl.href);
                setSiteName(loginUrl.hostname.split(".")[0] || "");
                setSiteInitials(
                    loginUrl.hostname.slice(0, 1).toUpperCase() +
                        loginUrl.hostname.slice(1, 2)
                );
                setCloudSiteId(
                    mostRecentActivity.object.rootContainerIdentifier.id
                );
            } else if (availableProductSites[0]) {
                const loginUrl = constructLoginUrl(
                    availableProductSites[0].url
                );
                setSiteUrl(loginUrl.href);
                setSiteName(loginUrl.hostname.split(".")[0] || "");
                setSiteInitials(
                    loginUrl.hostname.slice(0, 1).toUpperCase() +
                        loginUrl.hostname.slice(1, 2)
                );
                setCloudSiteId(availableProductSites[0].cloudId);
            } else {
                setSiteUrl(START_URL);
                setSiteName("");
            }
        });
    }, []);

    return {
        splitName,
        hasMultipleSites,
        siteName,
        siteUrl,
        siteInitials,
        cloudSiteId
    };
};
